import { useEffect, useRef, useState } from 'react'
import { Avatar, Box, Group, Paper, Stack, Switch, Text, Tooltip } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { useTranslation } from 'react-i18next'
import { getInitials } from 'helpers/getInitials'
import { DeliverableInfo } from './DeliverableInfo'
import { SelfAssessmentInfo } from './SelfAssessmentInfo'
import { TeacherAssessmentInput } from './TeacherAssessmentInput'
import type { AssessmentItem, DrawerStudent } from '../types'

/* Flex-based row layout (rewrite, was CSS Grid).
 *
 * Student section uses `flex: 1 1 0` so it absorbs any remaining space inside
 * the card — no empty gap on the right when the drawer is wider than the
 * combined fixed widths. The deliverable/self-assessment/teacher sections have
 * fixed flex-basis so columns line up across rows.
 *
 * Shared with BulkAssessRow via the COL_* exports to guarantee the score
 * picker on both rows sits at the exact same X. */
export const ROW_COMPACT_BREAKPOINT = '(max-width: 72em)'
export const ROW_SECTION_GAP = 16
export const COL_DELIVERABLE = 170
export const COL_SELF_ASSESSMENT = 195
export const COL_TEACHER = 324

interface StudentAssessmentRowProps {
  item: AssessmentItem
  student: DrawerStudent
  showDeliverableColumn: boolean
  showActivityNameInDeliverable: boolean
  onScoreChange: (score: number) => void
  onToggleStudentEnabled: (enabled: boolean) => void
}

/** Sets `truncated=true` when the wrapped element overflows its container. */
function useTextTruncated<T extends HTMLElement>() {
  const ref = useRef<T | null>(null)
  const [truncated, setTruncated] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const check = () => setTruncated(el.scrollWidth > el.clientWidth + 1)
    check()
    const ro = new ResizeObserver(check)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  return { ref, truncated }
}

function StudentName({ name }: { name: string }) {
  const { ref, truncated } = useTextTruncated<HTMLDivElement>()
  return (
    <Tooltip label={name} disabled={!truncated} withinPortal multiline position="top" width={260}>
      <Text
        ref={ref}
        weight={600}
        size="md"
        lineClamp={1}
        style={{ minWidth: 0, cursor: truncated ? 'help' : 'default' }}
      >
        {name}
      </Text>
    </Tooltip>
  )
}

interface TeacherSectionProps {
  enabled: boolean
  score: number
  color: 'green' | 'blue'
  size: number
  alignSwitchTo: 'right' | 'apart'
  disabledMessageAlign: 'left' | 'center'
  rubrics?: Record<string, string>
  onScoreChange: (score: number) => void
  onToggleEnabled: (enabled: boolean) => void
}

function TeacherSection({
  enabled,
  score,
  color,
  size,
  alignSwitchTo,
  disabledMessageAlign,
  rubrics,
  onScoreChange,
  onToggleEnabled,
}: TeacherSectionProps) {
  const { t } = useTranslation()
  return (
    <Stack spacing={6}>
      <Group position={alignSwitchTo} spacing="sm" noWrap mb={14}>
        <Text size="sm" color="dimmed" style={{ whiteSpace: 'nowrap' }}>
          {t('arc_teacher_assessment_drawer.teacher_assessment_label')}
        </Text>
        <Switch
          checked={enabled}
          onChange={e => onToggleEnabled(e.currentTarget.checked)}
          size="sm"
          label={t('arc_teacher_assessment_drawer.enable_assessment')}
          color={color}
          styles={{ label: { whiteSpace: 'nowrap' } }}
        />
      </Group>
      <TeacherAssessmentInput
        value={score}
        onChange={onScoreChange}
        disabled={!enabled}
        color={color}
        size={size}
        rubrics={rubrics}
      />
      {!enabled && (
        <Text
          size="sm"
          color="dimmed"
          italic
          align={disabledMessageAlign}
          data-testid="student-disabled-msg"
        >
          {t('arc_teacher_assessment_drawer.item_disabled_for_student')}
        </Text>
      )}
    </Stack>
  )
}

/**
 * One row inside the curriculum item card. Desktop layout puts every section
 * side-by-side via flex; the student section grows so the row fills the card
 * with no trailing gap. Mobile collapses everything into a vertical stack.
 *
 * The criteria slot inside `TeacherAssessmentInput` reserves height so the row
 * doesn't jump when a score is selected/cleared or the row is enabled/disabled.
 */
export function StudentAssessmentRow({
  item,
  student,
  showDeliverableColumn,
  showActivityNameInDeliverable,
  onScoreChange,
  onToggleStudentEnabled,
}: StudentAssessmentRowProps) {
  const { t } = useTranslation()
  const isMobile = useMediaQuery(ROW_COMPACT_BREAKPOINT)
  const ps = item.perStudent[student.id]
  if (!ps) return null

  const enabled = !ps.score.doNotAssess
  const color = item.curriculumKind === 'content' ? 'green' : 'blue'
  const name = student.fullName || t('arc_teacher_assessment_drawer.student_label')

  const studentHeader = (
    <Group spacing="sm" noWrap style={{ minWidth: 0, flex: '1 1 0' }}>
      <Avatar src={student.avatarUrl || undefined} radius="xl" size="md">
        {getInitials(student.fullName)}
      </Avatar>
      <Box style={{ minWidth: 0, flex: 1 }}>
        <StudentName name={name} />
      </Box>
    </Group>
  )

  const deliverable = showDeliverableColumn ? (
    <DeliverableInfo
      deliverable={ps.deliverable}
      showActivityName={showActivityNameInDeliverable}
      activityTitle={item.activityTitle}
    />
  ) : null

  const selfAssessment = (
    <SelfAssessmentInfo
      studentScore={ps.score.studentScore}
      studentAssessed={ps.score.studentAssessed}
    />
  )

  if (isMobile) {
    return (
      <Paper withBorder radius="sm" p="md" style={{ opacity: enabled ? 1 : 0.7 }}>
        <Stack spacing="md">
          {studentHeader}
          {deliverable}
          {selfAssessment}
          <TeacherSection
            enabled={enabled}
            score={ps.score.masterScore}
            color={color}
            size={48}
            alignSwitchTo="apart"
            disabledMessageAlign="center"
            rubrics={item.rubrics}
            onScoreChange={onScoreChange}
            onToggleEnabled={onToggleStudentEnabled}
          />
        </Stack>
      </Paper>
    )
  }

  return (
    <Paper withBorder radius="sm" p="md" style={{ opacity: enabled ? 1 : 0.7 }}>
      <Box
        data-testid="student-assessment-row-grid"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: ROW_SECTION_GAP,
          width: '100%',
        }}
      >
        {studentHeader}
        {deliverable && (
          <Box style={{ flex: `0 0 ${COL_DELIVERABLE}px`, minWidth: 0 }}>{deliverable}</Box>
        )}
        <Box style={{ flex: `0 0 ${COL_SELF_ASSESSMENT}px`, minWidth: 0 }}>{selfAssessment}</Box>
        <Box style={{ flex: `0 0 ${COL_TEACHER}px` }}>
          <TeacherSection
            enabled={enabled}
            score={ps.score.masterScore}
            color={color}
            size={56}
            alignSwitchTo="right"
            disabledMessageAlign="left"
            rubrics={item.rubrics}
            onScoreChange={onScoreChange}
            onToggleEnabled={onToggleStudentEnabled}
          />
        </Box>
      </Box>
    </Paper>
  )
}
